<template>
  <v-container fluid>
    <v-card class="elevation-2">
      <v-toolbar color="primary" dark flat>
        <v-icon class="mr-2">mdi-store</v-icon>
        <v-toolbar-title>Concesionarios</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-col cols="3" class="mr-3" v-if="$vuetify.breakpoint.mdAndUp">
          <v-text-field v-model="search" label="Buscar concesionario..." prepend-inner-icon="mdi-magnify"
            hide-details clearable rounded single-line filled dense dark></v-text-field>
        </v-col>
        <v-btn icon @click="openForm(null)" title="Nuevo concesionario"><v-icon>mdi-plus</v-icon></v-btn>
        <v-btn icon @click="load" title="Actualizar"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-toolbar>

      <v-data-table :headers="headers" :items="dealers" :search="search" :loading="loading"
        item-key="id" :items-per-page="10" :footer-props="{ itemsPerPageOptions: [10, 25, 50] }" no-data-text="Aún no hay concesionarios en el grupo">
        <template v-slot:item.name="{ item }">
          <div class="font-weight-medium">{{ item.name }}</div>
          <div class="text-caption grey--text" v-if="item.business_name">{{ item.business_name }}</div>
        </template>
        <template v-slot:item.conditions="{ item }">
          <v-chip v-for="c in item.conditions" :key="c" x-small class="mr-1" color="primary" outlined>{{ conditionLabel(c) }}</v-chip>
          <span v-if="!item.conditions.length" class="grey--text text-caption">Sin condiciones</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="item.status === STATUS_ACTIVE ? 'success' : 'grey'" dark>
            {{ item.status === STATUS_ACTIVE ? 'Activo' : 'Inactivo' }}
          </v-chip>
        </template>
        <template v-slot:item.action="{ item }">
          <div class="d-inline-flex">
            <v-btn small color="deep-purple" dark class="mr-2" :disabled="item.status !== STATUS_ACTIVE"
              :loading="entering === item.id" @click="enter(item)">
              <v-icon left small>mdi-login</v-icon>
              Entrar
            </v-btn>
            <v-btn icon small color="info" @click="viewDetails(item)" title="Ver detalles"><v-icon>mdi-eye</v-icon></v-btn>
            <v-btn icon small color="primary" @click="openForm(item)" title="Editar"><v-icon>mdi-pencil</v-icon></v-btn>
            <v-btn icon small :color="item.status === STATUS_ACTIVE ? 'error' : 'success'" @click="toggleStatus(item)"
              :title="item.status === STATUS_ACTIVE ? 'Desactivar' : 'Activar'">
              <v-icon>{{ item.status === STATUS_ACTIVE ? 'mdi-cancel' : 'mdi-check-circle' }}</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-store</v-icon>
          {{ editing ? 'Editar concesionario' : 'Nuevo concesionario' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-form ref="form" v-model="valid">
            <v-text-field v-model="form.name" label="Nombre comercial *" outlined dense :rules="[required]"></v-text-field>
            <v-text-field v-model="form.business_name" label="Razón social" outlined dense></v-text-field>
            <v-row dense>
              <v-col cols="6"><v-text-field v-model="form.cif" label="CIF" outlined dense></v-text-field></v-col>
              <v-col cols="6"><v-text-field v-model="form.city" label="Población" outlined dense></v-text-field></v-col>
            </v-row>
            <v-text-field v-model="form.address" label="Dirección" outlined dense></v-text-field>
            <div class="text-subtitle-2 mb-1">Condiciones</div>
            <div class="d-flex">
              <v-checkbox v-for="c in CONDITIONS" :key="c.value" v-model="form[c.value]" :label="c.text" class="mr-6 mt-0" hide-details></v-checkbox>
            </div>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false" :disabled="saving">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!valid" :loading="saving" @click="save">
            <v-icon left>mdi-content-save</v-icon>
            {{ editing ? 'Guardar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import GrupoPanelService from '@/services/GrupoPanelService'
import { CONDITIONS, STATUS_ACTIVE, STATUS_INACTIVE, conditionLabel, apiData, apiError } from '@/grupo'

const emptyForm = () => ({ name: '', business_name: '', cif: '', city: '', address: '', vn: true, vo: true, taller: false })

export default {
  name: 'GrupoDealers',

  data: () => ({
    CONDITIONS,
    STATUS_ACTIVE,
    dealers: [],
    loading: false,
    search: '',
    dialog: false,
    editing: null,
    valid: false,
    saving: false,
    entering: null,
    form: emptyForm(),
    headers: [
      { text: 'Concesionario', value: 'name' },
      { text: 'CIF', value: 'cif' },
      { text: 'Población', value: 'city' },
      { text: 'Condiciones', value: 'conditions', sortable: false },
      { text: 'Usuarios', value: 'users_count', align: 'center' },
      { text: 'Vehículos', value: 'vehicles_count', align: 'center' },
      { text: 'Estado', value: 'status', align: 'center' },
      { text: 'Acciones', value: 'action', sortable: false, align: 'end' }
    ]
  }),

  mounted () {
    this.load()
  },

  methods: {
    conditionLabel,
    required: v => !!v || 'Obligatorio',

    async load () {
      this.loading = true
      try {
        this.dealers = apiData(await GrupoPanelService.dealers()) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar los concesionarios'))
      } finally {
        this.loading = false
      }
    },

    viewDetails (dealer) {
      this.$router.push({ name: 'GrupoDealerDetail', params: { dealer_id: dealer.id } })
    },

    openForm (dealer) {
      this.editing = dealer
      this.form = dealer ? {
        name: dealer.name,
        business_name: dealer.business_name,
        cif: dealer.cif,
        city: dealer.city,
        address: dealer.address,
        vn: dealer.conditions.includes('vn'),
        vo: dealer.conditions.includes('vo'),
        taller: dealer.conditions.includes('taller')
      } : emptyForm()
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },

    async save () {
      this.saving = true
      const payload = { ...this.form, vn: this.form.vn ? 1 : 0, vo: this.form.vo ? 1 : 0, taller: this.form.taller ? 1 : 0 }
      try {
        if (this.editing) {
          await GrupoPanelService.updateDealer(this.editing.id, payload)
          this.$toast.success('Concesionario actualizado')
        } else {
          await GrupoPanelService.createDealer(payload)
          this.$toast.success('Concesionario creado')
        }
        this.dialog = false
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo guardar el concesionario'))
      } finally {
        this.saving = false
      }
    },

    async toggleStatus (dealer) {
      const activate = dealer.status !== STATUS_ACTIVE
      const ok = await this.$confirm(activate
        ? `¿Activar ${dealer.name}?`
        : `¿Desactivar ${dealer.name}? Los concesionarios se desactivan, no se borran.`, { title: activate ? 'Activar' : 'Desactivar' })
      if (!ok) return
      try {
        await GrupoPanelService.setDealerStatus(dealer.id, activate ? STATUS_ACTIVE : STATUS_INACTIVE)
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo cambiar el estado'))
      }
    },

    // 5.12 Entrar en el dealer como su Manager
    async enter (dealer) {
      this.entering = dealer.id
      try {
        await this.$store.dispatch('ENTER_DEALER', dealer.id)
        // Recarga completa para cargar el entorno del dealer
        window.location.href = '/vehiculo'
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo entrar en el concesionario'))
        this.entering = null
      }
    }
  }
}
</script>
